/**
 * ScheduleWorkoutDialog Component
 * Dialog to select a day from the training plan to schedule a workout
 */

import { CalendarToday, Close, Schedule, Warning } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { TrainingPlan, Workout } from '@shared/types';
import React, { useEffect, useState } from 'react';
import apiClient from '../../services/api';
import { logger } from '../../utils/logger';
import { findWorkoutByDate, getAllWorkouts } from '../../utils/planUtils';

interface ScheduleWorkoutDialogProps {
  open: boolean;
  onClose: () => void;
  workoutToSchedule: {
    id: string;
    name: string;
    minTime: number;
    load: number;
    type: 'easy' | 'hard';
    sportType: 'running' | 'cycling';
    environment?: 'indoor' | 'outdoor';
  };
  onSuccess?: (message: string) => void;
}

interface PreviewData {
  date: string;
  currentWorkout: Workout;
  newWorkout: Partial<Workout>;
  warnings: string[];
}

export const ScheduleWorkoutDialog: React.FC<ScheduleWorkoutDialogProps> = ({
  open,
  onClose,
  workoutToSchedule,
  onSuccess,
}) => {
  const [trainingPlan, setTrainingPlan] = useState<TrainingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  // Load training plan when dialog opens
  useEffect(() => {
    if (open) {
      loadTrainingPlan();
    }
  }, [open]);

  const loadTrainingPlan = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getStoredTrainingPlan();
      if (response.success && response.data && response.data.weeks) {
        setTrainingPlan(response.data);
        // Default to today's date if it's in the plan
        const today = new Date().toISOString().split('T')[0];
        const todayWorkout = findWorkoutByDate(response.data, today);
        if (todayWorkout) {
          setSelectedDate(today);
        }
      } else {
        setError('No training plan found. Generate a plan first.');
      }
    } catch (err: any) {
      logger.error('Failed to load training plan', err);
      setError(err.message || 'Failed to load training plan');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get available dates from training plan (future dates only)
  const getAvailableDates = (): { date: string; workout: Workout }[] => {
    if (!trainingPlan) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const allWorkouts = getAllWorkouts(trainingPlan);
    return allWorkouts
      .filter(workout => {
        const workoutDate = new Date(workout.training_date);
        workoutDate.setHours(0, 0, 0, 0);
        return workoutDate >= today;
      })
      .map(workout => ({ date: workout.training_date, workout }));
  };

  const availableDates = getAvailableDates();

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setPreviewData(null);
  };

  const handlePreview = () => {
    if (!selectedDate || !trainingPlan) return;

    const workoutResult = findWorkoutByDate(trainingPlan, selectedDate);
    if (!workoutResult) return;
    const currentWorkout = workoutResult.workout;

    const warnings: string[] = [];

    // Check if replacing a different sport type
    if (currentWorkout.sportType !== workoutToSchedule.sportType) {
      warnings.push(
        `Replacing ${currentWorkout.sportType || 'running'} workout with ${workoutToSchedule.sportType} workout`
      );
    }

    // Check if replacing a harder workout with easier
    if (currentWorkout.type === 'hard' && workoutToSchedule.type === 'easy') {
      warnings.push('Replacing a hard workout with an easy workout');
    }

    // Check TSS difference
    const tssDiff = Math.abs((currentWorkout.TSS || 0) - workoutToSchedule.load);
    if (tssDiff > 30) {
      warnings.push(
        `Significant TSS change: ${currentWorkout.TSS || 0} → ${workoutToSchedule.load}`
      );
    }

    setPreviewData({
      date: selectedDate,
      currentWorkout,
      newWorkout: {
        name: workoutToSchedule.name,
        total_duration: workoutToSchedule.minTime,
        TSS: workoutToSchedule.load,
        type: workoutToSchedule.type,
        sportType: workoutToSchedule.sportType,
        environment: workoutToSchedule.environment,
      },
      warnings,
    });

    if (warnings.length > 0) {
      setShowConfirmDialog(true);
    } else {
      handleConfirmSchedule();
    }
  };

  const handleConfirmSchedule = async () => {
    if (!selectedDate) return;

    setScheduling(true);
    setShowConfirmDialog(false);
    setError(null);

    try {
      // 🔍 DIAGNOSTIC: Log before sending request
      logger.info('🔍 SCHEDULING WORKOUT - REQUEST', {
        selectedDate,
        workoutId: workoutToSchedule.id,
        workoutName: workoutToSchedule.name,
        workoutType: workoutToSchedule.type,
        sportType: workoutToSchedule.sportType,
      });

      const response = await apiClient.scheduleWorkout(selectedDate, workoutToSchedule.id);

      if (response.success) {
        logger.info('Workout scheduled successfully', {
          date: selectedDate,
          workoutId: workoutToSchedule.id,
        });
        onSuccess?.(`${workoutToSchedule.name} scheduled for ${formatDate(selectedDate)}`);
        onClose();
      } else {
        setError(response.message || 'Failed to schedule workout');
      }
    } catch (err: any) {
      logger.error('Failed to schedule workout', err);
      setError(err.message || 'Failed to schedule workout');
    } finally {
      setScheduling(false);
    }
  };

  const handleClose = () => {
    setSelectedDate('');
    setPreviewData(null);
    setShowConfirmDialog(false);
    setError(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !showConfirmDialog}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" />
            <Typography variant="h6">Schedule Workout</Typography>
            <IconButton
              onClick={handleClose}
              aria-label="Close dialog"
              size="small"
              sx={{ ml: 'auto' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Workout being scheduled */}
              <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Workout to Schedule
                </Typography>
                <Typography variant="h6">{workoutToSchedule.name}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    label={workoutToSchedule.sportType === 'running' ? 'Run' : 'Bike'}
                    size="small"
                    color={workoutToSchedule.sportType === 'running' ? 'primary' : 'secondary'}
                  />
                  <Chip
                    label={`${workoutToSchedule.minTime} min`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip label={`TSS ${workoutToSchedule.load}`} size="small" variant="outlined" />
                </Box>
              </Box>

              <Divider />

              {/* Date selector */}
              <FormControl fullWidth>
                <InputLabel>Select Date</InputLabel>
                <Select
                  value={selectedDate}
                  onChange={e => handleDateSelect(e.target.value)}
                  label="Select Date"
                  aria-label="Select date to schedule workout"
                >
                  {availableDates.map(({ date, workout }) => (
                    <MenuItem key={date} value={date}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography sx={{ minWidth: 100 }}>{formatDate(date)}</Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ flexGrow: 1, textAlign: 'right' }}
                        >
                          {workout.name} ({workout.total_duration}min)
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Preview of what will be replaced */}
              {selectedDate && !previewData && (
                <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current workout on {formatDate(selectedDate)}
                  </Typography>
                  {(() => {
                    const current = availableDates.find(d => d.date === selectedDate)?.workout;
                    if (!current) return null;
                    return (
                      <Box sx={{ mt: 1 }}>
                        <Typography>{current.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={current.sportType === 'running' ? 'Run' : 'Bike'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${current.total_duration} min`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip label={`TSS ${current.TSS}`} size="small" variant="outlined" />
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={scheduling}>
            Cancel
          </Button>
          <Button
            onClick={handlePreview}
            variant="contained"
            disabled={!selectedDate || loading || scheduling}
            startIcon={scheduling ? <CircularProgress size={16} /> : <Schedule />}
            aria-label="Schedule workout"
          >
            {scheduling ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for warnings */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Confirm Schedule Change
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                You're about to replace <strong>{previewData.currentWorkout.name}</strong> with{' '}
                <strong>{workoutToSchedule.name}</strong> on {formatDate(previewData.date)}.
              </Typography>

              {previewData.warnings.length > 0 && (
                <List dense>
                  {previewData.warnings.map((warning, index) => (
                    <ListItemButton key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={warning}
                        primaryTypographyProps={{ color: 'warning.main' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmSchedule}
            variant="contained"
            color="warning"
            disabled={scheduling}
            startIcon={scheduling ? <CircularProgress size={16} /> : <Schedule />}
            aria-label="Confirm schedule"
          >
            {scheduling ? 'Scheduling...' : 'Confirm Schedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduleWorkoutDialog;
